import { UnifiedNewsEntry } from '../../news/types/UnifiedNewsEntry'
import { TopGame } from '../../top-game/types/TopGame'
import { Game } from '../types/Game'

export const collectUniqueIds = (ids: Array<string | number | undefined | null>): string[] => {
  return [...new Set(ids.filter((id): id is string | number => id !== undefined && id !== null && id !== '').map(String))].sort()
}

export const mapResolvedGameNames = (games?: Array<Pick<Game, 'id' | 'appId' | 'resolvedName'>>): Record<string, string> => {
  if (!games) {
    return {}
  }

  return games.reduce((namesById, game) => {
    if (!game.resolvedName) {
      return namesById
    }

    if (game.id) {
      namesById[String(game.id)] = game.resolvedName
    }

    if (game.appId) {
      namesById[String(game.appId)] = game.resolvedName
    }

    return namesById
  }, {} as Record<string, string>)
}

export const lookupResolvedGameName = (namesById: Record<string, string>, ...ids: Array<string | number | undefined | null>): string | undefined => {
  for (const id of ids) {
    if (id === undefined || id === null || id === '') {
      continue
    }

    const resolvedName = namesById[String(id)]
    if (resolvedName) {
      return resolvedName
    }
  }

  return undefined
}

export const overlayTopGameNames = (topGames: TopGame[] | undefined, namesById: Record<string, string>): TopGame[] | undefined => {
  if (!topGames || !Object.keys(namesById).length) {
    return topGames
  }

  let didChange = false
  const nextTopGames = topGames.map((topGame) => {
    const resolvedName = lookupResolvedGameName(namesById, topGame.gameId, topGame.id, topGame.appId)
    if (!resolvedName || resolvedName === topGame.name) {
      return topGame
    }

    didChange = true
    return new TopGame({ ...topGame, name: resolvedName } as TopGame)
  })

  return didChange ? nextTopGames : topGames
}

export const collectNewsEntryGameIds = (entries: UnifiedNewsEntry[]): string[] => {
  const ids: Array<string | number | undefined | null> = []

  entries.forEach((entry) => {
    ids.push(entry.feedEntry?.gameId, entry.feedEntry?.entryData?.gameId, entry.feedEntry?.entryData?.appId)
    entry.feedEntry?.entryData?.similarGames?.forEach((similarGame) => {
      ids.push(similarGame.gameId, similarGame.appId)
    })
  })

  return collectUniqueIds(ids)
}

export const overlayNewsEntryGameNames = (entries: UnifiedNewsEntry[], namesById: Record<string, string>): UnifiedNewsEntry[] => {
  if (!entries.length || !Object.keys(namesById).length) {
    return entries
  }

  let didChange = false
  const nextEntries = entries.map((entry) => {
    const resolvedName = lookupResolvedGameName(namesById, entry.feedEntry?.gameId, entry.feedEntry?.entryData?.gameId, entry.feedEntry?.entryData?.appId)
    const cardNameChanged = Boolean(resolvedName && resolvedName !== entry.feedEntry?.entryData?.game)

    const originalSimilarGames = entry.feedEntry?.entryData?.similarGames
    let similarGames = originalSimilarGames
    if (originalSimilarGames?.length) {
      const mappedSimilarGames = originalSimilarGames.map((similarGame) => {
        const similarResolvedName = lookupResolvedGameName(namesById, similarGame.gameId, similarGame.appId)
        if (!similarResolvedName || similarResolvedName === similarGame.name) {
          return similarGame
        }

        return { ...similarGame, name: similarResolvedName }
      })

      if (mappedSimilarGames.some((similarGame, index) => similarGame !== originalSimilarGames[index])) {
        similarGames = mappedSimilarGames
      }
    }

    if (!cardNameChanged && similarGames === originalSimilarGames) {
      return entry
    }

    didChange = true
    return {
      ...entry,
      feedEntry: {
        ...entry.feedEntry,
        entryData: {
          ...entry.feedEntry.entryData,
          ...(cardNameChanged ? { game: resolvedName } : {}),
          ...(similarGames !== originalSimilarGames ? { similarGames } : {}),
        },
      },
    }
  })

  return didChange ? nextEntries : entries
}
